import { IMAGES } from "@/data/products";

export const TAGLINE = "Track What Matters.";
export const SUBLINE = "Unlimited range. Zero subscriptions. Magnetic by design. The compact Find My tracker built for your car, trailer, tools and every valuable thing in between.";

export const NAV_LINKS = [
  { label: "Shop", to: "/shop" },
  { label: "Vehicles", to: "/vehicles" },
  { label: "Fleets", to: "/fleet" },
  { label: "Equipment", to: "/equipment" },
  { label: "Personal", to: "/personal" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "About", to: "/about" },
  { label: "Support", to: "/support" },
];

// Platform-aware product facts. Everything on the site should trace back here.
// Access via techFor(platform) or useTech() — never hardcode Apple strings.
const TECH_APPLE = {
  network: "Apple Find My network (Bluetooth)",
  notGps: "Unlimited range. Tracksy pairs with the global Apple Find My network — if a Tracksy is anywhere in the world, you can find it from your Find My app.",
  compat: "Works with iPhone and iPad. Set up in Apple Find My.",
  subscription: "No subscription. No new app. Pairs straight to the Find My app already on your iPhone.",
  battery: "Replaceable CR2025 coin-cell battery (170 mAh) — one tap in Find My tells you when to swap it. No charging, no cables.",
  water: "Not rated for water resistance — mount it in a dry, protected spot.",
  range: "Unlimited range. Across town or across the world, Tracksy stays findable through Apple's global Find My network — zero distance cap, zero cell bills.",
  size: "Tracker 3.17 × 3.17 × 0.8 cm · Magnetic case 4.6 × 3.5 × 0.7 cm · ABS case",
  kit: "Each tag ships with a matte black magnetic protective case (ABS) that attaches straight to steel, plus two adhesive metal sheets for non-metal surfaces.",
};

const TECH_ANDROID = {
  network: "Google Find Hub network (Bluetooth)",
  notGps: "Unlimited range. Tracksy pairs with the global Google Find Hub network — if a Tracksy is anywhere in the world, you can find it from your Find Hub app.",
  compat: "Works with Android. Set up in Google Find Hub.",
  subscription: "No subscription. No new app. Pairs straight to the Find Hub app already on your Android phone.",
  battery: "Replaceable CR2025 coin-cell battery (170 mAh) — one tap in Find Hub tells you when to swap it. No charging, no cables.",
  water: "Not rated for water resistance — mount it in a dry, protected spot.",
  range: "Unlimited range. Across town or across the world, Tracksy stays findable through Google's global Find Hub network — zero distance cap, zero cell bills.",
  size: "Tracker 3.17 × 3.17 × 0.8 cm · Magnetic case 4.6 × 3.5 × 0.7 cm · ABS case",
  kit: "Each tag ships with a matte black magnetic protective case (ABS) that attaches straight to steel, plus two adhesive metal sheets for non-metal surfaces.",
};

export const TECH = { apple: TECH_APPLE, android: TECH_ANDROID };
export const techFor = (platform) => TECH[platform] || TECH_APPLE;

export const WHY_MAGNETIC = [
  { id: "car", label: "Car", img: IMAGES.carBoot },
  { id: "van", label: "Van", img: IMAGES.fleetVans },
  { id: "trailer", label: "Trailer", img: IMAGES.trailer },
  { id: "toolbox", label: "Toolbox", img: IMAGES.toolbox },
  { id: "equipment", label: "Equipment", img: IMAGES.generator },
  { id: "bike", label: "Bike", img: IMAGES.bike },
  { id: "outdoor", label: "Outdoor gear", img: IMAGES.outdoor },
];

export const INDUSTRIES = [
  { id: "construction", name: "Construction", desc: "Keep visibility on site vehicles, trailers and shared equipment moving between jobs." },
  { id: "tradies", name: "Tradies", desc: "Attach to the ute, the trailer and the toolbox so your livelihood is easier to locate." },
  { id: "delivery", name: "Delivery", desc: "Add a layer of visibility to vans, cages and returnable crates." },
  { id: "transport", name: "Transport", desc: "Tag trailers, dollies and spare equipment that move between depots." },
  { id: "hire", name: "Equipment Hire", desc: "Compact tracking on generators, compactors and hire gear out with customers." },
  { id: "property", name: "Property Services", desc: "Cleaning, maintenance and strata teams tracking vehicles and kit." },
  { id: "landscaping", name: "Landscaping", desc: "Mowers, trailers and tools that travel to a different yard every day." },
  { id: "rental", name: "Rental Businesses", desc: "Bikes, scooters, caravans and party hire assets out in the field." },
  { id: "service", name: "Service Fleets", desc: "Plumbers, electricians and technicians with vehicles full of valuable tools." },
  { id: "smallbiz", name: "Small Business", desc: "Any business with a few vehicles and assets worth keeping an eye on." },
];

// Why Tracksy — platform-aware. Picks the right network/app per phrase.
const whyFor = (p) => {
  const net = p === "android" ? "Google Find Hub" : "Apple Find My";
  const netShort = p === "android" ? "Find Hub" : "Find My";
  const phone = p === "android" ? "Android phone" : "iPhone";
  return [
    { id: "range", title: "Unlimited Range", desc: `Pairs with ${net} to track your car, trailer or motorcycle anywhere — no distance cap, no cellular bills, no monthly fees. Ever.` },
    { id: "compact", title: "Mini Size", desc: "Just 3.17 cm across and 8 mm thin — slides under a seat, inside a wheel arch or onto a keychain. Totally unnoticeable." },
    { id: "magnetic", title: "Magnetic Case", desc: "Matte black ABS case with a built-in magnetic cover — snaps instantly to any metal surface. No brackets, no screws, no permanent install." },
    { id: "setup", title: "60-Second Setup", desc: `Open ${netShort} → tap Add Item → done. No new app, no new account, no learning curve. Already on every ${phone}.` },
    { id: "uses", title: "One Tag, Endless Uses", desc: "Vehicles, trailers, equipment, luggage, bikes and keys — pop it off, stick it on something else. Reusable forever." },
    { id: "business", title: "Fleet-Ready Pricing", desc: "Multi-packs built for teams. Volume rewards from 10 trackers, dedicated account manager at 100+." },
    { id: "support", title: "Human Support", desc: "Real people answering setup and compatibility questions — not a bot queue." },
  ];
};
export const whyTracksyFor = whyFor;
export const WHY_TRACKSY = whyFor("apple"); // legacy default export

const stepsFor = (p) => {
  const net = p === "android" ? "Google Find Hub" : "Apple Find My";
  const netShort = p === "android" ? "Find Hub" : "Find My";
  const phone = p === "android" ? "Android phone" : "iPhone";
  return [
    { step: "01", title: "Attach", desc: "Place Tracksy on the vehicle, trailer, equipment or item you want to track. Use the metal sheet on non-metal surfaces." },
    { step: "02", title: "Connect", desc: `Open ${netShort} on your ${phone} → Items → Add Item → Other Supported Item. Tracksy appears in your Items list.` },
    { step: "03", title: "Find", desc: `See its last reported location on the map, play a sound when nearby, or enable Lost Mode in ${netShort}.` },
  ];
};
export const stepsForPlatform = stepsFor;
export const STEPS = stepsFor("apple"); // legacy default

const faqsFor = (p) => {
  const net = p === "android" ? "Google Find Hub" : "Apple Find My";
  const netShort = p === "android" ? "Find Hub" : "Find My";
  const phone = p === "android" ? "Android phone" : "iPhone";
  const compat = p === "android"
    ? "an Android 9+ phone with Google Play services and the Find Hub app enabled"
    : "an iPhone or iPad running iOS / iPadOS 14.8 or later and uses the Find My app";
  const otherPlatform = p === "android" ? "iPhone users" : "Android users";
  const otherNetwork = p === "android" ? "Apple Find My" : "Google Find Hub";
  return [
    { q: "Is Tracksy a GPS tracker?", a: `No. Tracksy is a Bluetooth tracker that works with the ${net} network. It does not have a cellular connection and does not provide continuous live GPS tracking.` },
    { q: `Does it work with ${p === "android" ? "iPhone" : "Android"}?`, a: `This Tracksy is built for ${p === "android" ? "Android" : "Apple"}. If you're on ${p === "android" ? "iPhone" : "Android"}, switch the storefront to ${otherNetwork} using the toggle at the top — we offer a dedicated ${otherNetwork} tracker for ${otherPlatform}.` },
    { q: "Is there a subscription or separate app?", a: `No. Tracksy uses the ${netShort} app already on your ${phone}. There is no subscription and no Tracksy app to install.` },
    { q: "How does the magnet work on non-metal surfaces?", a: "Each tag includes two adhesive metal sheets. Stick a sheet where you want the tracker to live and the magnetic case snaps to it." },
    { q: "Is the battery rechargeable?", a: `No — Tracksy uses a replaceable CR2025 coin-cell battery (170 mAh). When ${netShort} shows the battery is low, pop the case open and swap in a new CR2025. No cables, no charging.` },
    { q: "What is the range?", a: `Unlimited. Tracksy has no distance cap of its own — ${net}'s global network relays its location securely, so it can be found across town or across the world.` },
    { q: "Can I use it on a vehicle or trailer?", a: "Yes — many customers attach it inside a wheel arch, on a chassis rail or trailer drawbar. Choose a position that is protected from direct road spray and impact." },
    { q: "Which finishes are available?", a: "Obsidian Black is available in every pack. Ceramic White is currently available in the 4-pack and 8-pack." },
    { q: "Do bigger orders get a discount?", a: "Yes. Volume rewards apply automatically in the cart based on the total number of trackers: 10+ unlocks 5% off plus 2 free trackers, 25+ unlocks 8% off plus 3 free trackers and priority support, 50+ unlocks 10% off plus 5 free trackers, a VIP support line and advance replacement, and 100+ unlocks 12% off plus 8 free trackers with a dedicated account manager and 36-month warranty — or request tailored pricing." },
    { q: "I need a lot of trackers. How do I order?", a: "Order any quantity directly online — mix packs and set the quantity you need. For orders of 100+ devices, use the Fleet pricing form and we'll come back with tailored pricing and a purchase-order process." },
  ];
};
export const faqsForPlatform = faqsFor;
export const FAQS = faqsFor("apple"); // legacy default

// Business policies shown on the site. Update these to match your actual terms.
export const POLICIES = [
  { id: "shipping", title: "Shipping", desc: "Tracked shipping on every order. Free shipping on orders over $50." },
  { id: "returns", title: "Returns", desc: "30-day change-of-mind returns on unopened packs." },
  { id: "warranty", title: "Warranty", desc: "12-month warranty against manufacturing defects." },
];

export const PERSONAL_USES = [
  { label: "Luggage", img: IMAGES.luggage },
  { label: "Backpacks & outdoor gear", img: IMAGES.outdoor },
  { label: "Bikes", img: IMAGES.bike },
  { label: "Toolboxes", img: IMAGES.toolbox },
  { label: "Keys & valuables", img: IMAGES.keys },
  { label: "Motorbikes", img: IMAGES.motorbike },
];
